import { Easing, Group, Tween } from '@tweenjs/tween.js';
import type { Graph3DBounds, Graph3DVector3 } from '../core/graph.types';
import { Vector3 } from 'three';
import type { PerspectiveCamera } from 'three';

export interface RuntimeOrbitControlsLike {
  target: Vector3;
  update: () => void;
}

interface TweenVector3State {
  x: number;
  y: number;
  z: number;
}

export interface ViewportTweenController {
  stop: () => void;
  animatePan: (params: {
    camera: PerspectiveCamera;
    controls: RuntimeOrbitControlsLike;
    nextTarget: Graph3DVector3;
    duration?: number;
  }) => void;
  animateFitView: (params: {
    camera: PerspectiveCamera;
    controls: RuntimeOrbitControlsLike;
    bounds: Graph3DBounds;
    targetCenter: Graph3DVector3;
    padding?: number;
    duration?: number;
    preserveViewDirection?: boolean;
  }) => void;
  animateViewport: (params: {
    camera: PerspectiveCamera;
    controls: RuntimeOrbitControlsLike;
    viewport: {
      target: Graph3DVector3;
      cameraPosition: Graph3DVector3;
    };
    duration?: number;
  }) => void;
}

const DEFAULT_VIEWPORT_TWEEN_MS = 320;
const DEFAULT_FIT_VIEW_PADDING = 88;
const MIN_CAMERA_DISTANCE = 320;
const MIN_HALF_SPAN = 120;
const MIN_HALF_DEPTH = 24;
const FIT_VIEW_DISTANCE_SAFETY = 1.06;
const DEFAULT_VIEW_DIRECTION = new Vector3(0.72, 0.42, 1).normalize();
const WORLD_UP = new Vector3(0, 1, 0);

export function resolveFitViewPlacement({
  camera,
  bounds,
  targetCenter,
  padding = DEFAULT_FIT_VIEW_PADDING,
  preserveViewDirection = false,
  currentTarget,
  currentCameraPosition,
}: {
  camera: PerspectiveCamera;
  bounds: Graph3DBounds;
  targetCenter: Graph3DVector3;
  padding?: number;
  preserveViewDirection?: boolean;
  currentTarget?: Graph3DVector3;
  currentCameraPosition?: Graph3DVector3;
}) {
  const halfX = Math.max(bounds.size.x / 2 + padding, MIN_HALF_SPAN);
  const halfY = Math.max(bounds.size.y / 2 + padding, MIN_HALF_SPAN);
  const halfZ = Math.max(bounds.size.z / 2 + padding * 0.45, MIN_HALF_DEPTH);
  const verticalTan = Math.tan((camera.fov * Math.PI) / 360);
  const horizontalTan = verticalTan * camera.aspect;
  const corner = new Vector3();
  const viewDirection = preserveViewDirection
    ? new Vector3(
        (currentCameraPosition?.x ?? 0) - (currentTarget?.x ?? 0),
        (currentCameraPosition?.y ?? 0) - (currentTarget?.y ?? 0),
        (currentCameraPosition?.z ?? 0) - (currentTarget?.z ?? 0)
      ).normalize()
    : DEFAULT_VIEW_DIRECTION.clone();
  if (viewDirection.lengthSq() === 0) {
    viewDirection.copy(DEFAULT_VIEW_DIRECTION);
  }
  const viewForward = viewDirection.clone().multiplyScalar(-1);
  const viewRight = viewForward.clone().cross(WORLD_UP).normalize();
  const safeViewRight =
    viewRight.lengthSq() > 0.0001
      ? viewRight
      : new Vector3(1, 0, 0).cross(viewDirection).normalize();
  const safeViewUp = safeViewRight.clone().cross(viewForward).normalize();
  let requiredDistance = MIN_CAMERA_DISTANCE;

  for (const xSign of [-1, 1]) {
    for (const ySign of [-1, 1]) {
      for (const zSign of [-1, 1]) {
        corner.set(halfX * xSign, halfY * ySign, halfZ * zSign);
        const projectedX = Math.abs(corner.dot(safeViewRight));
        const projectedY = Math.abs(corner.dot(safeViewUp));
        const forwardExtent = corner.dot(viewDirection);

        requiredDistance = Math.max(
          requiredDistance,
          projectedX / Math.max(horizontalTan, 0.001) + forwardExtent,
          projectedY / Math.max(verticalTan, 0.001) + forwardExtent
        );
      }
    }
  }

  const distance = Math.max(
    requiredDistance * FIT_VIEW_DISTANCE_SAFETY,
    MIN_CAMERA_DISTANCE
  );

  return {
    nextTarget: {
      x: targetCenter.x,
      y: targetCenter.y,
      z: targetCenter.z,
    },
    nextCameraPosition: {
      x: targetCenter.x + viewDirection.x * distance,
      y: targetCenter.y + viewDirection.y * distance,
      z: targetCenter.z + viewDirection.z * distance,
    },
  };
}

/**
 * 统一管理 3D 视口补间。
 *
 * 这里单独抽成工具而不是把 tween 逻辑散落在 Scene 里，后续节点、边、面板等
 * 动画也可以复用同一套 Group / requestAnimationFrame 驱动与中断策略。
 */
export function createViewportTweenController(): ViewportTweenController {
  const tweenGroup = new Group();
  let frameId: number | null = null;
  let activeTargetTween: Tween<TweenVector3State> | null = null;
  let activeCameraTween: Tween<TweenVector3State> | null = null;

  const stopFrameLoop = () => {
    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
      frameId = null;
    }
  };

  const tick = (time: number) => {
    const hasActiveTweens = tweenGroup.update(time);
    if (!hasActiveTweens) {
      frameId = null;
      return;
    }
    frameId = window.requestAnimationFrame(tick);
  };

  const ensureFrameLoop = () => {
    if (frameId !== null) {
      return;
    }
    frameId = window.requestAnimationFrame(tick);
  };

  const stop = () => {
    activeTargetTween?.stop();
    activeCameraTween?.stop();
    activeTargetTween = null;
    activeCameraTween = null;
    tweenGroup.removeAll();
    stopFrameLoop();
  };

  const animatePan: ViewportTweenController['animatePan'] = ({
    camera,
    controls,
    nextTarget,
    duration = DEFAULT_VIEWPORT_TWEEN_MS,
  }) => {
    const startTarget = controls.target.clone();
    const startCamera = camera.position.clone();
    const targetVector = startTarget
      .clone()
      .set(nextTarget.x, nextTarget.y, controls.target.z);
    const cameraDelta = targetVector.clone().sub(startTarget);
    const endCamera = startCamera.clone().add(cameraDelta);

    if (
      targetVector.distanceToSquared(startTarget) < 1 &&
      endCamera.distanceToSquared(startCamera) < 1
    ) {
      return;
    }

    stop();

    const targetState: TweenVector3State = {
      x: startTarget.x,
      y: startTarget.y,
      z: startTarget.z,
    };
    const cameraState: TweenVector3State = {
      x: startCamera.x,
      y: startCamera.y,
      z: startCamera.z,
    };

    const syncViewport = () => {
      controls.target.set(targetState.x, targetState.y, targetState.z);
      camera.position.set(cameraState.x, cameraState.y, cameraState.z);
      controls.update();
    };

    activeTargetTween = new Tween(targetState, tweenGroup)
      .to(
        {
          x: targetVector.x,
          y: targetVector.y,
          z: targetVector.z,
        },
        duration
      )
      .easing(Easing.Cubic.Out)
      .onUpdate(syncViewport)
      .onStop(() => {
        activeTargetTween = null;
      })
      .onComplete(() => {
        activeTargetTween = null;
      });

    activeCameraTween = new Tween(cameraState, tweenGroup)
      .to(
        {
          x: endCamera.x,
          y: endCamera.y,
          z: endCamera.z,
        },
        duration
      )
      .easing(Easing.Cubic.Out)
      .onUpdate(syncViewport)
      .onStop(() => {
        activeCameraTween = null;
      })
      .onComplete(() => {
        activeCameraTween = null;
      });

    activeTargetTween.start();
    activeCameraTween.start();
    ensureFrameLoop();
  };

  const animateFitView: ViewportTweenController['animateFitView'] = ({
    camera,
    controls,
    bounds,
    targetCenter,
    padding = DEFAULT_FIT_VIEW_PADDING,
    duration = DEFAULT_VIEWPORT_TWEEN_MS,
    preserveViewDirection = false,
  }) => {
    const { nextTarget, nextCameraPosition } = resolveFitViewPlacement({
      camera,
      bounds,
      targetCenter,
      padding,
      preserveViewDirection,
      currentTarget: {
        x: controls.target.x,
        y: controls.target.y,
        z: controls.target.z,
      },
      currentCameraPosition: {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z,
      },
    });

    stop();

    const targetState: TweenVector3State = {
      x: controls.target.x,
      y: controls.target.y,
      z: controls.target.z,
    };
    const cameraState: TweenVector3State = {
      x: camera.position.x,
      y: camera.position.y,
      z: camera.position.z,
    };

    const syncViewport = () => {
      controls.target.set(targetState.x, targetState.y, targetState.z);
      camera.position.set(cameraState.x, cameraState.y, cameraState.z);
      camera.lookAt(targetState.x, targetState.y, targetState.z);
      controls.update();
    };

    activeTargetTween = new Tween(targetState, tweenGroup)
      .to(nextTarget, duration)
      .easing(Easing.Cubic.InOut)
      .onUpdate(syncViewport)
      .onStop(() => {
        activeTargetTween = null;
      })
      .onComplete(() => {
        activeTargetTween = null;
      });

    activeCameraTween = new Tween(cameraState, tweenGroup)
      .to(nextCameraPosition, duration)
      .easing(Easing.Cubic.InOut)
      .onUpdate(syncViewport)
      .onStop(() => {
        activeCameraTween = null;
      })
      .onComplete(() => {
        activeCameraTween = null;
      });

    activeTargetTween.start();
    activeCameraTween.start();
    ensureFrameLoop();
  };

  const animateViewport: ViewportTweenController['animateViewport'] = ({
    camera,
    controls,
    viewport,
    duration = DEFAULT_VIEWPORT_TWEEN_MS,
  }) => {
    stop();

    const targetState: TweenVector3State = {
      x: controls.target.x,
      y: controls.target.y,
      z: controls.target.z,
    };
    const cameraState: TweenVector3State = {
      x: camera.position.x,
      y: camera.position.y,
      z: camera.position.z,
    };

    const syncViewport = () => {
      controls.target.set(targetState.x, targetState.y, targetState.z);
      camera.position.set(cameraState.x, cameraState.y, cameraState.z);
      camera.lookAt(targetState.x, targetState.y, targetState.z);
      controls.update();
    };

    activeTargetTween = new Tween(targetState, tweenGroup)
      .to(viewport.target, duration)
      .easing(Easing.Cubic.InOut)
      .onUpdate(syncViewport)
      .onStop(() => {
        activeTargetTween = null;
      })
      .onComplete(() => {
        activeTargetTween = null;
      });

    activeCameraTween = new Tween(cameraState, tweenGroup)
      .to(viewport.cameraPosition, duration)
      .easing(Easing.Cubic.InOut)
      .onUpdate(syncViewport)
      .onStop(() => {
        activeCameraTween = null;
      })
      .onComplete(() => {
        activeCameraTween = null;
      });

    activeTargetTween.start();
    activeCameraTween.start();
    ensureFrameLoop();
  };

  return {
    stop,
    animatePan,
    animateFitView,
    animateViewport,
  };
}
